/**
 * Helpers to normalize icon-font labels in semantic snapshots.
 *
 * Some apps render icon-only controls as private-use Unicode glyphs
 * (e.g. Font Awesome: "\uf067") which are unreadable in plain text output.
 *
 * @module
 * @see /docs/snapshot-formatter.md (when created)
 */

const PRIVATE_USE_GLYPH_RE = /[\uE000-\uF8FF]/g;
const HAS_PRIVATE_USE_GLYPH_RE = /[\uE000-\uF8FF]/;

const ICON_ONLY_ROLES = new Set(['button', 'link']);

// Common Font Awesome private-use code points frequently seen in snapshots.
const GLYPH_LABEL_OVERRIDES: Record<string, string> = {
  '\uf067': 'Add',
  '\uf128': 'Help',
  '\uf013': 'Settings',
  '\uf2bd': 'Profile',
};

function mapKnownGlyphs(text: string): string | null {
  const mapped: string[] = [];
  for (const ch of text) {
    if (!HAS_PRIVATE_USE_GLYPH_RE.test(ch)) continue;
    const label = GLYPH_LABEL_OVERRIDES[ch];
    if (!label) return null;
    mapped.push(label);
  }

  return mapped.length > 0 ? mapped.join(' ') : null;
}

/**
 * Normalize icon-font/private-use glyph text into usable labels.
 *
 * Rules:
 * - If text has no private-use glyphs, keep as-is.
 * - If text mixes glyphs + readable text, strip glyphs and keep readable text.
 * - If text is glyph-only and all glyphs are known, map to human labels.
 * - If text is glyph-only and unknown on icon-only controls, use "(icon)".
 */
const SVG_SPRITE_RE = /^\/[^\s]*\.svg#(.+)$/;

export function normalizeIconLabelText(role: string, text: string): string {
  const trimmed = text.trim();
  if (!trimmed) {
    return ICON_ONLY_ROLES.has(role) ? '(icon)' : '';
  }

  // Strip SVG sprite paths that leaked through as labels (e.g., "/static/icons/fa.svg#grid")
  const spriteMatch = trimmed.match(SVG_SPRITE_RE);
  if (spriteMatch) return spriteMatch[1];

  if (!HAS_PRIVATE_USE_GLYPH_RE.test(trimmed)) {
    return trimmed;
  }

  const withoutGlyphs = trimmed.replace(PRIVATE_USE_GLYPH_RE, ' ').replace(/\s+/g, ' ').trim();
  if (withoutGlyphs) {
    return withoutGlyphs;
  }

  const mapped = mapKnownGlyphs(trimmed);
  if (mapped) {
    return mapped;
  }

  return ICON_ONLY_ROLES.has(role) ? '(icon)' : '';
}

/**
 * Zero-width / BOM characters that silently break naive text matching. MUI (and
 * other frameworks) embed U+200B between a filter chip's label and its value
 * (`"Transaction type<U+200B>All (13)"`), so `getByText("Transaction type")` and
 * `textContent.includes("Transaction type")` both miss unless these are stripped.
 */
const ZERO_WIDTH_RE = /[\u200B-\u200D\uFEFF]/g;

/**
 * Normalize text for robust comparison/matching: strip private-use icon glyphs
 * and zero-width/BOM characters, convert NBSP to a normal space, and collapse
 * runs of whitespace. Case is preserved — lowercase at the comparison site.
 *
 * This is the driver-side source of truth for text normalization. The page-side
 * `browser_scan` traversal runs inside `page.evaluate` (browser context, no
 * imports) and must INLINE a byte-identical implementation (see `pageSideScan`'s
 * `NORM` and `stampWinner`'s `norm` in `playwright-client/fast-drive-scan.ts`).
 * `icon-label-normalizer.test.ts` pins THIS function's behavior via a cases
 * table; keep the inline copies in sync with it when editing either side.
 */
export function normalizeComparableText(text: string | null | undefined): string {
  return (text ?? '')
    .replace(PRIVATE_USE_GLYPH_RE, ' ')
    .replace(ZERO_WIDTH_RE, '')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const STYLED_WRAPPER_RE = /^Styled\(\w+\)$/;
const MUI_CLASS_RE = /^Mui\w+-[\w-]+$/;
const HAS_ASCII_ALNUM_RE = /[A-Za-z0-9]/;

/**
 * Detect labels that are visual noise rather than meaningful text.
 *
 * Matches:
 * - MUI styled component names like "Styled(svg)", "Styled(span)"
 * - MUI CSS class names like "MuiButtonBase-root", "MuiSvgIcon-fontSizeMedium"
 * - Emoji-only strings with no ASCII alphanumeric chars (e.g. "🅶 + 🅾")
 */
export function isNoisyLabel(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  if (STYLED_WRAPPER_RE.test(trimmed)) return true;
  if (MUI_CLASS_RE.test(trimmed)) return true;
  // Private-use glyphs are handled separately by normalizeIconLabelText
  if (HAS_PRIVATE_USE_GLYPH_RE.test(trimmed)) return false;
  if (!HAS_ASCII_ALNUM_RE.test(trimmed)) return true;
  return false;
}
